import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import {
  enablePush, disablePush, pushEnabledHere, pushSupported, pushNeedsInstallFirst,
} from '../push';
import { useT } from '../i18n.jsx';

// Turning notifications on, from a place a person actually chose to press.
//
// It is never offered automatically. A browser refuses a permission prompt that was not asked for,
// and once somebody has said no there is no way to ask again from code -- so a prompt spent at a bad
// moment is spent for good. This sits next to Install and waits.
//
// Four states worth distinguishing, because three of them are dead ends and saying so is the whole
// job of this component:
//   on              already registered on this device
//   off             can be turned on right here
//   install-first   iOS delivers web push only to a PWA on the home screen; a Safari tab cannot
//   unsupported     nothing to offer, so nothing is shown
const PushToggle = ({ schoolId, compact = false }) => {
  const { t } = useT();
  const [state, setState] = useState('checking');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    let alive = true;
    pushSupported().then((ok) => {
      if (!alive) return;
      if (!ok) { setState('unsupported'); return; }
      if (pushNeedsInstallFirst()) { setState('install-first'); return; }
      setState(pushEnabledHere() ? 'on' : 'off');
    });
    return () => { alive = false; };
  }, []);

  const toggle = useCallback(async () => {
    if (!schoolId || busy) return;
    setBusy(true);
    setNote('');

    if (state === 'on') {
      await disablePush(schoolId);
      setState('off');
    } else {
      const res = await enablePush(schoolId);
      if (res.ok) {
        setState('on');
      } else if (res.reason === 'denied') {
        // Nothing further can be done from here: the browser will not ask twice.
        setNote(t('push.blocked'));
      } else if (res.reason === 'install-first') {
        setState('install-first');
      } else {
        setNote(t('push.failed'));
      }
    }
    setBusy(false);
    // t is a dependency: the note is written when the button is pressed, so a callback holding
    // last language's t would answer in the language the person just left.
  }, [schoolId, state, busy, t]);

  if (state === 'checking' || state === 'unsupported') return null;

  if (state === 'install-first') {
    // Honest rather than hidden: the reason it is unavailable is fixable, and the fix is one screen
    // away in the Install entry sitting right next to this.
    return compact ? null : (
      <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', lineHeight: 1.6, marginBottom: '0.75rem' }}>
        {t('push.installFirst')}
      </p>
    );
  }

  const on = state === 'on';
  const Icon = busy ? Loader2 : (on ? Bell : BellOff);
  const label = on ? t('push.on') : t('push.turnOn');

  if (compact) {
    return (
      <button
        type="button"
        onClick={toggle}
        className="btn-outline"
        title={label}
        aria-label={label}
        style={{
          width: 'auto', padding: '0.45rem', display: 'flex', alignItems: 'center', flexShrink: 0,
          color: on ? 'var(--color-primary)' : undefined,
          backgroundColor: on ? 'var(--color-nav-active)' : undefined,
        }}
      >
        <Icon size={16} className={busy ? 'spin' : undefined} />
      </button>
    );
  }

  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <button
        type="button"
        onClick={toggle}
        className="btn-outline"
        style={{
          width: '100%', padding: '0.5rem', fontSize: '0.875rem',
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.4rem',
          color: on ? 'var(--color-primary)' : undefined,
          backgroundColor: on ? 'var(--color-nav-active)' : undefined,
        }}
      >
        <Icon size={16} /> {label}
      </button>
      {note && (
        <p style={{ fontSize: '0.75rem', color: 'var(--color-danger-text)', lineHeight: 1.6, marginTop: '0.4rem' }}>
          {note}
        </p>
      )}
    </div>
  );
};

export default PushToggle;
